// SPDX-License-Identifier: GPL-3.0-only
// OpenSaga7897237: gizmos_gizpanel.cpp GizPanel_CanUsePanel/FindNearest/
// Use/MoveCode/GetAbsTargetPos, gizpanel.cpp Reset/GetOutput/Activate,
// characters.cpp SetProtocolDroidInterfaceAction, move.cpp:2946 approach.
// Ordinary protocol/astromech panels only. Host supplies original terrain
// probes and actual AN3 action durations; unknown bindings cannot finish use.
const TAU = Math.PI * 2;
function vec(p) { const a = Array.isArray(p) ? p : [p?.x, p?.y, p?.z]; if (a.length !== 3 || !a.every(Number.isFinite)) throw new Error('Invalid panel coordinate.'); return a.slice(); }
function rotated(p, yaw) { const a = (yaw & 65535) * TAU / 65536, c = Math.cos(a), s = Math.sin(a); return [p[0] * c + p[2] * s, p[1], p[2] * c - p[0] * s]; }
const dist2 = (a, b) => a.reduce((sum, v, i) => sum + (v - b[i]) ** 2, 0);
const lookup = (table, id) => table instanceof Map ? table.get(id) : table?.[id];

// These are just the verified bits relevant to panel eligibility. Merge the
// original txt_file base first (TC14 inherits protocol from C3PO.TXT).
export function panelAbilityFlags(config) {
  const v = config?.values ?? config ?? {}; let flags = 0;
  if (v.droid === true) flags |= 0x10;
  if (v.protocol === true) flags |= 0x30;
  if (v.astromech === true) flags |= 0x50;
  if (v.bounty_hunter === true) flags |= 0x1000000;
  return flags;
}

export function canUsePanelAbility(panel, actor) {
  if (!panel || !actor) return false;
  if (actor.superWeirdo === true) return true;
  const flags = actor.modelFlags;
  if (!Number.isInteger(flags)) return false;
  if (panel.modelVariant === 0) return !!(flags & 0x40) || (flags & 0x1000010) === 0x1000010;
  if (panel.modelVariant === 1) return !!(flags & 0x20) || (flags & 0x1000010) === 0x1000010;
  return false;
}

export function createPanelState(gizmos, { floorProbe = null, animationDuration = () => null, progress = {}, gizmoFlags = {} } = {}) {
  if (!Array.isArray(gizmos?.panels) || gizmos.panels.length > 512 || typeof animationDuration !== 'function' || (floorProbe !== null && typeof floorProbe !== 'function')) throw new Error('Invalid panel runtime.');
  const records = [], byName = new Map(), byId = new Map(), events = [];
  const emit = (type, r, data = {}) => events.push({ type, id: r.id, name: r.name, kind: 'panel', ...data });
  function probe(point) {
    const hit = floorProbe?.(point.slice(), .2);
    const height = typeof hit === 'number' ? hit : hit?.height;
    return Number.isFinite(height) && height !== 2000000 ? height : null;
  }
  function resetGeometry(r) {
    const floorOffset = rotated([0, 0, .4], r.yRotation + 0x8000);
    r.floorPosition = r.position.map((v, i) => v + floorOffset[i]);
    const floorY = probe(r.floorPosition);
    r.floorPosition[1] = floorY ?? 2000000;
    const target = rotated(r.targetOffset, r.yRotation);
    const targetProbe = [target[0] + r.position[0], r.position[1], target[2] + r.position[2]];
    const targetY = probe(targetProbe);
    r.targetOffset[1] = targetY === null ? 2000000 : targetY + .001;
    r.terrainResolved = floorY !== null && targetY !== null;
    r.ramp = 0;
  }
  for (const source of gizmos.panels) {
    const name = String(source.name).toLowerCase();
    if (!name || byName.has(name) || byId.has(source.id)) throw new Error('Duplicate/invalid panel reference.');
    const saved = records.length < 32 ? lookup(progress, source.id) ?? {} : {}, flags = lookup(gizmoFlags, source.id) ?? lookup(gizmoFlags, name) ?? {};
    const r = { ...source, source, position: vec(source.position), targetOffset: vec(source.targetOffset),
      enabled: saved.enabled ?? flags.enabled ?? true, visible: saved.visible ?? flags.visible ?? true,
      complete: saved.complete ?? false, actor: null, remaining: 0, action: null, ramp: 0, unsupported: source.modelVariant > 1 ? ['bounty/storm panel runtime'] : [] };
    resetGeometry(r); records.push(r); byName.set(name, r); byId.set(r.id, r);
  }
  function resolve(ref) {
    if (typeof ref === 'string') return byId.get(ref) ?? byName.get(ref.toLowerCase()) ?? null;
    if (ref?.type && ref.type.toLowerCase() !== 'panel') return null;
    return ref ? byId.get(ref.id) ?? byName.get(String(ref.name).toLowerCase()) ?? null : null;
  }
  function targetPosition(r, forUse = false) {
    const offset = forUse && r.modelVariant === 0 ? [-.04, r.targetOffset[1], -.3] : forUse && r.modelVariant === 1 ? [.035, r.targetOffset[1], -.25] : r.targetOffset;
    const p = rotated(offset, r.yRotation); p[0] += r.position[0]; p[2] += r.position[2]; return p;
  }
  function nearest(actor) {
    if (!actor || actor.alive === false || actor.health === 0 || actor.playerActive === false) return null;
    const position = vec(actor.position); let best = null, distance = 1e9;
    for (const r of records) {
      if (!r.enabled || !r.visible || r.complete || r.actor || !r.terrainResolved || r.unsupported.length || !canUsePanelAbility(r, actor)) continue;
      const d = dist2(position, targetPosition(r)); if (d < distance) { best = r; distance = d; }
    }
    return best ? { record: best, distanceSquared: distance } : null;
  }
  function canUse(ref, actor) {
    const r = resolve(ref), selected = nearest(actor);
    if (!r || selected?.record !== r || ![-1, 1, 2].includes(actor.context ?? -1)) return false;
    const radius = actor.planarRadius ?? actor.collisionRadius;
    return Number.isFinite(radius) && radius > 0 && selected.distanceSquared < ((.25 + radius) * r.targetScale) ** 2;
  }
  return {
    records, resolve, nearest, canUse, targetPosition: (ref, forUse = false) => { const r = resolve(ref); return r ? targetPosition(r, forUse) : null; },
    use(ref, actor) {
      const r = resolve(ref); if (!canUse(ref, actor)) return false;
      let action = 'interface1';
      if ((actor.modelFlags & 0x20) && [1, 2, 3].includes(actor.protocolParts)) action = `interface${5 - actor.protocolParts}`;
      const duration = animationDuration(actor, action);
      if (duration === null || duration === undefined || !Number.isFinite(duration) || duration < 0) { emit('blocked', r, { reason: 'original interface animation duration unresolved', action }); return false; }
      r.actor = actor; r.action = action; r.remaining = duration > 0 ? duration : 2; r.missingClip = duration === 0; r.ramp = 0;
      emit('started', r, { actor, action, duration: r.remaining, heading: r.yRotation * TAU / 65536, hideWeapon: true, targetPosition: targetPosition(r, true) });
      return true;
    },
    release(ref, actor = null) { const r = resolve(ref); if (!r || !r.actor || (actor && r.actor !== actor)) return false; const owner = r.actor; r.actor = null; r.remaining = 0; emit('released', r, { actor: owner }); return true; },
    update(dt) {
      if (!Number.isFinite(dt) || dt < 0 || dt > 1) throw new Error('Invalid panel time.');
      for (const r of records) {
        if (!r.actor) continue;
        const actor = r.actor;
        if (actor.alive === false || actor.health === 0 || actor.playerActive === false) { this.release(r.id, actor); continue; }
        // Both native animation-playing branches decrement this timer; only
        // the approach ramp requires playing/missing-clip state.
        if (r.missingClip || actor.panelAnimationPlaying === true) r.ramp = Math.min(1, Math.fround(r.ramp + dt));
        r.remaining = Math.fround(r.remaining - dt);
        const target = targetPosition(r, true), position = vec(actor.position), rate = r.ramp * 10 + 5;
        emit('pose', r, { actor, action: r.action, targetPosition: target, targetVelocity: [(target[0] - position[0]) * rate, 0, (target[2] - position[2]) * rate], heading: r.yRotation * TAU / 65536, remaining: Math.max(0, r.remaining) });
        if (r.remaining <= 0) { r.complete = true; r.actor = null; r.remaining = 0; emit('complete', r, { actor, action: r.action }); }
      }
    },
    activate(ref, on) { const r = resolve(ref); if (!r) return false; r.enabled = !!on; if (on) { r.visible = true; r.complete = false; resetGeometry(r); } return true; },
    setVisibility(ref, on) { const r = resolve(ref); if (!r) return false; r.visible = !!on; emit('visibility', r, { visible: r.visible }); return true; },
    // Native callback ignores output index and ignoreActivation; it still
    // requires both VISIBLE and TRACK_PLAYER even for OutputOnly observers.
    getOutput(ref) { const r = resolve(ref); return r ? r.unsupported.length ? null : r.visible && r.enabled && r.complete : null; },
    drawStates() { return records.map(r => ({ id: r.id, position: r.position.slice(), heading: r.yRotation * TAU / 65536, visible: r.visible,
      modelNames: r.hideBase ? [] : [r.alternateModel ? 'R2_base1' : r.modelVariant === 1 ? 'C3PO_base' : 'R2_base',
        `${r.modelVariant === 1 ? 'C3PO' : 'R2'}_light_${r.complete ? 'on' : 'off'}`], targetPosition: targetPosition(r), targetScale: r.targetScale, finished: r.complete })); },
    drainEvents() { return events.splice(0); },
    saveProgress() { return Object.fromEntries(records.slice(0, 32).map(r => [r.id, { complete: r.complete, visible: r.visible, enabled: r.enabled }])); },
    limitations: ['bounty/storm panel use', 'camera/head tracking and animated portrait rendering', 'native panel terrain supplied by host', 'audio/hints and protocol limb visuals'],
  };
}
